<!-- src/components/cml/CmlPopupActionBar.vue -->
<template>
  <div class="cml-popup-action-bar" data-test="popup-action-bar">
    <!-- Show this CML in the OTHER viewer (zoom/isolate; opens that viewer only if not already on it). In 3D → offer 360; in 360 → offer 3D. -->
    <v-tooltip top>
      <template #activator="{ on, attrs }">
        <v-btn icon small v-on="on" v-bind="attrs"
               :disabled="!canShowInOtherViewer"
               data-test="popup-icon-show-in-viewer"
               @click="onShowInOtherViewer">
          <v-icon>{{ otherViewerIcon }}</v-icon>
        </v-btn>
      </template>
      <span>{{ otherViewerTooltip }}</span>
    </v-tooltip>

    <v-tooltip top>
      <template #activator="{ on, attrs }">
        <v-btn icon small v-on="on" v-bind="attrs"
               :disabled="isReadingLoading"
               data-test="popup-icon-reading"
               @click="onReadings">
          <!-- AVA-437 #5a — swap the gauge for a spinner while REQUEST_VIEW_DETAIL
               awaits the parent master tag fetch (registry-miss path), so the
               ~2s wait isn't dead time. -->
          <v-progress-circular v-if="isReadingLoading" indeterminate size="18" width="2" color="primary" />
          <v-icon v-else>mdi-gauge</v-icon>
        </v-btn>
      </template>
      <span>View readings</span>
    </v-tooltip>

    <v-menu offset-y :close-on-content-click="true" content-class="cml-popup-owned-overlay">
      <template #activator="{ on: menuOn, attrs: menuAttrs }">
        <v-tooltip top>
          <template #activator="{ on: tipOn, attrs: tipAttrs }">
            <v-btn icon small
                   v-on="{ ...menuOn, ...tipOn }" v-bind="{ ...menuAttrs, ...tipAttrs }"
                   data-test="popup-icon-add-task">
              <v-icon>mdi-calendar-plus</v-icon>
            </v-btn>
          </template>
          <span>Add to task</span>
        </v-tooltip>
      </template>
      <v-card elevation="8" class="cml-popup-task-menu">
        <v-list dense>
          <v-subheader>Create task in board</v-subheader>
          <v-divider />
          <template v-if="taskBoards.length">
            <v-list-item v-for="board in taskBoards" :key="board.id" @click="onCreateTaskInBoard(board.id)">
              <v-list-item-icon class="mr-2"><v-icon small color="primary">mdi-clipboard-plus-outline</v-icon></v-list-item-icon>
              <v-list-item-content><v-list-item-title>{{ board.name }}</v-list-item-title></v-list-item-content>
            </v-list-item>
          </template>
          <v-list-item v-else disabled>
            <v-list-item-title class="text--disabled">No boards available</v-list-item-title>
          </v-list-item>
          <v-divider />
          <v-list-item @click="onLinkToExistingTask">
            <v-list-item-icon class="mr-2"><v-icon small>mdi-link-variant</v-icon></v-list-item-icon>
            <v-list-item-content><v-list-item-title>Link to existing task</v-list-item-title></v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card>
    </v-menu>

    <!-- Documents — only the CML/TML-linked docs (AVA-437 Tier 2), prefetched on open so the
         button shows its state up-front: spinner while loading, disabled when none, direct-open
         for a single doc, menu for several. Split by count so the controlled menu never nests
         inside the tooltip activator (the Vuetify-2 nested-activator trap). -->
    <v-tooltip top v-if="docsLoading || documents.length <= 1">
      <template #activator="{ on, attrs }">
        <v-btn icon small v-on="on" v-bind="attrs"
               :disabled="docsLoading || documents.length === 0"
               data-test="popup-icon-document"
               @click="onDocumentButtonClick">
          <v-progress-circular v-if="docsLoading" indeterminate size="18" width="2" color="primary" />
          <v-icon v-else>mdi-file-document-outline</v-icon>
        </v-btn>
      </template>
      <span>{{ documentsTooltip }}</span>
    </v-tooltip>

    <v-menu v-else offset-y :close-on-content-click="true" content-class="cml-popup-owned-overlay">
      <template #activator="{ on: menuOn, attrs: menuAttrs }">
        <v-tooltip top>
          <template #activator="{ on: tipOn, attrs: tipAttrs }">
            <v-btn icon small
                   v-on="{ ...menuOn, ...tipOn }" v-bind="{ ...menuAttrs, ...tipAttrs }"
                   data-test="popup-icon-document">
              <v-icon>mdi-file-document-outline</v-icon>
            </v-btn>
          </template>
          <span>{{ documentsTooltip }}</span>
        </v-tooltip>
      </template>
      <v-card elevation="8" class="cml-popup-doc-menu">
        <v-list dense>
          <v-subheader>Documents</v-subheader>
          <v-divider />
          <v-list-item v-for="doc in documents" :key="doc.id" @click="openDoc(doc)">
            <v-list-item-icon class="mr-2"><v-icon small color="primary">mdi-file-document-outline</v-icon></v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ doc.documentNumber || doc.documentTitle || doc.fileName || 'Untitled' }}</v-list-item-title>
              <v-list-item-subtitle v-if="doc.documentType" class="text-caption text--secondary">{{ doc.documentType }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
import { navigateToViewerWithAction, VIEWER_TYPES } from '@/utils/viewerNavigationUtils';
import { POPUP_ACTION } from '@/components/cml/popupActions';
import { popupCardState } from '@/composables/usePopupCard';
import {
  assertDocEntityHighlightWhenRendered,
  focusEntityAcrossViewers,
  resolveEntityHighlightColor,
} from '@/composables/useCrossViewerSync';
import { log as debugLog, error as debugError } from '@/utils/debugLogger';
import documentsService from '@/features/Task/service/documents.service';
import { openAttachment } from '@/scripts/js/forgeviewer';
import { GET_DOCUMENT_BY_ID } from '@/utils/attachmentsAPI';

export default {
  name: 'CmlPopupActionBar',
  props: {
    vm: { type: Object, required: true },
    entityType: { type: String, default: 'cml' },
    entityId: { type: String, default: '' },
    sourceViewer: { type: String, default: null, validator: (v) => v === null || ['3d', '360', 'doc'].includes(v) }, // '3d' | '360' | 'doc'
  },
  data() {
    return { documents: [], docsLoading: false };
  },
  created() {
    this._docReqId = 0;
    this._openDocReqId = 0;
  },
  computed: {
    isInThreeD() { return this.sourceViewer === VIEWER_TYPES.MODEL_3D; },
    otherViewerIcon() { return this.isInThreeD ? 'mdi-image-filter-center-focus' : 'mdi-cube-outline'; },
    otherViewerTooltip() { return this.isInThreeD ? 'Show in 360' : 'Show in 3D'; },
    canShowInOtherViewer() {
      // In 3D we zoom to a 360 scan → need a world point. In 360 we isolate in 3D → need a placed dbId.
      const raw = (this.vm && this.vm.raw) || {};
      return this.isInThreeD ? !!raw.point : raw.forgeElementDbId != null;
    },
    taskBoards() {
      return this.$store.getters['TASK_MANAGER/GET_ENTRIES_BY_USER'] || [];
    },
    // AVA-437 #5a — true while REQUEST_VIEW_DETAIL awaits the parent master tag
    // fetch; drives the spinner + disabled state on the "View readings" button.
    isReadingLoading() {
      return this.$store.getters['CML_POPUP/isViewDetailLoading'];
    },
    // AVA-437 Tier 2 — documents are scoped to the CML (or, for a TmlPoint, to its parent CML
    // PLUS this TmlPoint via the link metadata). A TmlPoint carries no masterTagId of its own.
    cmlIdForDocs() {
      return this.entityType === 'tmlpoint' ? (this.vm && this.vm.cmlRecordId) || null : this.entityId;
    },
    tmlPointIdForDocs() {
      return this.entityType === 'tmlpoint' ? this.entityId : null;
    },
    // Label for a task scope seeded from this popup — CML number (CML popup) or TML position.
    taskScopeLabel() {
      return (this.vm && (this.vm.cmlNo || this.vm.positionLabel)) || '';
    },
    documentsTooltip() {
      if (this.docsLoading) return 'Loading documents…';
      if (!this.documents.length) return 'No linked documents';
      if (this.documents.length === 1) return 'Open linked document';
      return 'Linked documents';
    },
  },
  mounted() {
    this.prefetchDocuments();
  },
  watch: {
    // The popup card is a singleton — switching CML/TML markers updates props in place,
    // so re-prefetch the linked docs whenever the target entity changes.
    entityId() {
      this.prefetchDocuments();
    },
  },
  methods: {
    onReadings() {
      debugLog('[CmlPopupActionBar] readings', this.entityId);
      this.$emit(POPUP_ACTION.VIEW_READINGS);
    },
    onCreateTaskInBoard(boardId) {
      // AVA-437 Tier 2 — scope by the REAL CML id (the parent CML for a TmlPoint), never the
      // tmlPointId; tmlPointId rides along so a TML-aware scope can narrow further downstream.
      debugLog('[CmlPopupActionBar] create-task in board', boardId, this.cmlIdForDocs, this.tmlPointIdForDocs);
      this.$emit(POPUP_ACTION.CREATE_TASK, {
        boardId,
        cmlRecordId: this.cmlIdForDocs,
        tmlPointId: this.tmlPointIdForDocs,
        cmlNo: this.taskScopeLabel,
      });
    },
    onLinkToExistingTask() {
      debugLog('[CmlPopupActionBar] link-task', this.cmlIdForDocs, this.tmlPointIdForDocs);
      this.$emit(POPUP_ACTION.LINK_TASK, {
        cmlRecordId: this.cmlIdForDocs,
        tmlPointId: this.tmlPointIdForDocs,
        cmlNo: this.taskScopeLabel,
      });
    },
    onShowInOtherViewer() {
      const raw = (this.vm && this.vm.raw) || {};
      const forgeService = this.$store.state.forgeService;
      if (this.isInThreeD) {
        // In 3D → show this CML in 360: zoom to the nearest scan for its world point.
        if (!raw.point) return;
        const tagLike = { id: this.entityId, point: raw.point, positionImage360Id: raw.positionImage360Id || null };
        debugLog('[CmlPopupActionBar] show-in 360', this.entityId);
        navigateToViewerWithAction(VIEWER_TYPES.PANORAMA_360, async () => {
          if (!forgeService) return;
          // highlight: false — this CML draws its OWN coloured marker just below. Without it the
          // service also raises its generic highlight hotspot, and because `tagLike` carries no
          // `type` that hotspot falls through to the unlabelled 'general' pin, landing a second
          // ghost marker next to the real one. (a456fa72 added that call to this path.)
          await forgeService.zoomToTagByScanPosition(tagLike, { highlight: false });
          // Draw THIS CML's marker on the 360 via the 360-only hotspot manager — NOT
          // showCmlPointMarkers, which starts by clearing ALL 3D CML markers (we are
          // still in the 3D source viewer with the popup open). showCmlHotspots sets
          // _lastCmlInspection BEFORE its viewer guard, so this works both ways: if the
          // scan is already open the marker draws immediately; on a fresh open,
          // launchPanoViewerHandler creates panoramaViewer ~1s later and ends by calling
          // updateTagsAnnotationsInPanoViewer → updateAllHotspots, which re-renders
          // _lastCmlInspection (initialize() does not clear it). showCmlHotspots has no
          // showCml toggle gate, so this explicit "show this CML" action draws
          // regardless of the global CML display toggle. (AVA-437 #3)
          // Fallback: draw THIS CML's marker only when no tag-panel marker set is
          // already present — otherwise the tag-panel mixin owns the full colored
          // set (rendered via its isViewer360Ready watcher) and we must not clobber
          // it. Colour comes from _getCmlConditionColor (now int-aware). (AVA-437 #3)
          const panoHotspots = forgeService.panoramaHotspotManager;
          if (panoHotspots
              && typeof panoHotspots.showCmlHotspots === 'function'
              && typeof panoHotspots.hasCmlHotspots === 'function'
              && !panoHotspots.hasCmlHotspots()) {
            panoHotspots.showCmlHotspots({ cmlRecords: [raw] });
          }
        });
      } else {
        // In 360 → show this CML in 3D. Which master tag greys, which element carries the
        // condition colour, how the camera frames it and whether a point marker can be placed is
        // ONE decision, resolved server-side from the stable subject. This used to fetch the
        // master + CML colouring rows, run the worst-child-wins reducer and hand-build a ghost
        // paint map — a third copy of the arbitrator's own job.
        const cmlDbId = raw.forgeElementDbId;
        const entityId = this.entityId; // capture at click — the popup is a singleton; this.entityId mutates if the user switches CMLs mid-fetch
        if (cmlDbId == null) return;
        debugLog('[CmlPopupActionBar] show-in 3D (entity scope)', entityId);
        navigateToViewerWithAction(VIEWER_TYPES.MODEL_3D, async () => {
          if (this.entityId !== entityId) return; // user switched CMLs while the viewer opened
          const outcome = await focusEntityAcrossViewers({
            entityType: this.entityType,
            entityId,
            raw,
            rawPoint: raw.point || null,
            sourceViewer: this.sourceViewer,
            swapDocument: false,
            title: this.taskScopeLabel || null,
          });
          this.notifyFocusOutcome(outcome);
        });
      }
      // Popup intentionally STAYS OPEN — the marker channel now refuses rather than tearing down a
      // layer it does not own (which is what used to close this popup), navigateToViewerWithAction opens the target viewer
      // only if not already on it; the current viewer + marker remain and the rAF anchor
      // loop keeps the popup tracked as the layout reflows. Do NOT close the popup here.
    },
    /**
     * Say what actually happened, using the render receipt rather than the request.
     *
     * A refused marker is NOT an error: `foreignMarkerLayer` means another surface's marker set is
     * already on screen (this CML's marker is almost certainly in it) and `cmlMarkersHidden` means
     * the user's own display toggle is off. Either is only worth saying when the CML ended up with
     * no visible point at all — otherwise the notice would contradict what the user can see.
     */
    notifyFocusOutcome(outcome) {
      const notify = this.$dialog && this.$dialog.notify;
      if (!notify) return;
      const options = { position: 'top-right', timeout: 3000 };

      if (!outcome || outcome.status === 'unavailable') return;
      if (outcome.status === 'failed') {
        notify.warning('Could not load the 3D view for this CML.', options);
        return;
      }
      if (outcome.status === 'empty' || outcome.status === 'unrenderable') {
        notify.warning('No placed model elements for this CML.', options);
        return;
      }
      if (outcome.markerRefused === 'cmlMarkersHidden' && !outcome.markerVisible) {
        notify.info('CML markers are hidden by your display settings.', options);
      }
    },
    // Prefetch the CML/TML-linked documents when the popup targets a (new) entity, so the
    // button shows the right state before any click. The CmlId endpoint returns a flat
    // DocumentVM list (markup-link-scoped — no tag/task buckets to merge).
    prefetchDocuments() {
      const cmlId = this.cmlIdForDocs;
      if (!cmlId) { this.documents = []; this.docsLoading = false; return; }
      this.documents = [];
      this.docsLoading = true;
      const reqId = ++this._docReqId;
      const params = { AssetId: this.$store.state.assetId };
      if (this.tmlPointIdForDocs) params.tmlPointId = this.tmlPointIdForDocs;
      documentsService.getByCmlId(cmlId, params)
        .then((response) => {
          if (reqId !== this._docReqId) return;
          const data = (response && response.data) || response || [];
          this.documents = Array.isArray(data) ? data : [];
          debugLog(`[CmlPopupActionBar] linked documents for cml ${cmlId} (tml=${this.tmlPointIdForDocs || '-'}): ${this.documents.length}`);
        })
        .catch((err) => { debugError('[CmlPopupActionBar] getByCmlId failed', err); this.documents = []; })
        .finally(() => { if (reqId === this._docReqId) this.docsLoading = false; });
    },
    // Single linked doc → open it directly; none/loading → disabled no-op. The >1 case is
    // handled by the v-menu activator branch in the template (this handler isn't bound there).
    onDocumentButtonClick() {
      if (this.docsLoading || !this.documents.length) return;
      if (this.documents.length === 1) this.openDoc(this.documents[0]);
    },
    openDoc(doc) {
      // Mirrors nodeControls (selectPIdDocument → getDocumentById → openPIdInDocViewer):
      // the slim getByTagId list item only reliably carries `id`, so resolve the FULL
      // document by id FIRST, then open it by reference. Always .catch (no silent failures).
      debugLog('[CmlPopupActionBar] openDoc', doc && doc.id);
      const request = {
        id: ++this._openDocReqId,
        entityType: this.entityType,
        entityId: this.entityId,
      };
      return GET_DOCUMENT_BY_ID(doc.id)
        .then((fullDoc) => this.openResolvedDocument(fullDoc, request))
        .catch((err) => debugError('[CmlPopupActionBar] openDoc failed', err));
    },
    isDocumentOpenRequestStale(request) {
      return request.id !== this._openDocReqId
        || !popupCardState.isOpen
        || popupCardState.status !== 'Loaded'
        || popupCardState.entityType !== request.entityType
        || String(popupCardState.entityId) !== String(request.entityId)
        || this.entityType !== request.entityType
        || String(this.entityId) !== String(request.entityId);
    },
    openResolvedDocument(fullDoc, request) {
      if (this.isDocumentOpenRequestStale(request)) return undefined;
      if (!fullDoc) {
        this.$dialog.notify.warning('No document found.', { position: 'top-right', timeout: 3000 });
        return undefined;
      }
      return this.$store.dispatch('GET_DOCUMENT_BY_REFERENCE_ID', {
        documentId: fullDoc.documentId,
        storageReference: fullDoc.storageReference,
        name: fullDoc.fileName,
      }).then((forgeAttachment) => this.finishDocumentOpen(forgeAttachment, fullDoc, request));
    },
    finishDocumentOpen(forgeAttachment, fullDoc, request) {
      if (this.isDocumentOpenRequestStale(request)) return;
      openAttachment(forgeAttachment);
      assertDocEntityHighlightWhenRendered({
        entityType: request.entityType,
        entityId: request.entityId,
        // One shared resolver (`useCrossViewerSync`), so the document highlight can never
        // disagree with the 3D marker and the 360 hotspot.
        colorHex: resolveEntityHighlightColor({
          entityType: request.entityType,
          entityId: request.entityId,
          raw: this.vm && this.vm.raw,
        }),
        targetDocumentId: fullDoc.documentId,
        isStale: () => this.isDocumentOpenRequestStale(request),
      });
    },
  },
};
</script>

<style scoped>
.cml-popup-action-bar {
  display: flex;
  justify-content: center;
  align-items: center;
  column-gap: 16px;
  flex-wrap: wrap;
  padding: 8px 10px;
  background: oklch(0.97 0.006 145);
  border-top: 1px solid oklch(0.90 0.009 145);
}
</style>
